import { useRef, useState } from "react";
import { api } from "../lib/api";
import { useSpeech } from "../hooks/useSpeech";
import { Mic, Square, Loader2, Volume2, ArrowRight, TrendingUp, TrendingDown, Lightbulb, X, Keyboard } from "lucide-react";
import { toast } from "sonner";

export function ReplayCoach({ answer, onClose, onResult }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [retake, setRetake] = useState("");
  const [typed, setTyped] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const recRef = useRef(null);
  const { toggle, speaking, activeKey, loadingKey } = useSpeech();

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = e => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", new Blob(chunks, { type: "audio/webm" }), "retake.webm");
          const { data } = await api.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text?.trim()) { setRetake(data.text); await compare(data.text); }
          else toast.error("I didn't catch that — try again");
        } catch { toast.error("Transcription failed"); }
        finally { setTranscribing(false); }
      };
      rec.start(); recRef.current = rec; setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };
  const stop = () => { recRef.current?.stop(); setRecording(false); };

  const compare = async (text) => {
    const t = (text ?? retake).trim();
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post("/warmup/coach", { concept: answer.concept, question: answer.question, original: answer.answer, retake: t });
      setResult(data);
      onResult?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "ARIA couldn't compare"); }
    finally { setBusy(false); }
  };

  const delta = result && result.retake_clarity != null && result.original_clarity != null ? result.retake_clarity - result.original_clarity : null;

  return (
    <div className="mt-3 rounded-md border border-[var(--aria-primary)]/40 bg-[var(--aria-primary)]/5 p-4 fade-in" data-testid="replay-coach">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--aria-primary-glow)] font-semibold">Answer replay coach · {answer.concept}</div>
        <button onClick={onClose} data-testid="replay-coach-close" className="text-slate-400 hover:text-white"><X size={14} /></button>
      </div>

      {!result && (
        <>
          <p className="text-sm text-slate-300 mb-3">Say this answer again, out loud, in your own words. ARIA will compare it to your original for clarity and delivery.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={recording ? stop : start} disabled={busy || transcribing} data-testid="replay-coach-record"
              className={`btn px-4 py-2 text-sm inline-flex items-center gap-2 border disabled:opacity-50 ${recording ? "border-red-500 bg-red-500/20 text-red-200 animate-pulse" : "btn-primary"}`}>
              {transcribing ? <Loader2 size={14} className="animate-spin" /> : recording ? <Square size={14} /> : <Mic size={14} />}
              {transcribing ? "Transcribing…" : recording ? "Stop & compare" : "Record my retake"}
            </button>
            <button onClick={() => setTyped(!typed)} data-testid="replay-coach-type-toggle" className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"><Keyboard size={12} /> type instead</button>
            {busy && <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Comparing…</span>}
          </div>
          {typed && (
            <div className="flex gap-2 mt-3">
              <textarea value={retake} onChange={e => setRetake(e.target.value)} rows={2} placeholder="Type your retake…" data-testid="replay-coach-text"
                className="flex-1 px-3 py-2 rounded-md bg-white/5 border border-white/15 outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm text-white resize-none" />
              <button onClick={() => compare()} disabled={busy || retake.trim().length < 15} data-testid="replay-coach-submit" className="btn btn-primary px-3 disabled:opacity-40 flex items-center"><ArrowRight size={14} /></button>
            </div>
          )}
        </>
      )}

      {result && (
        <div data-testid="replay-coach-result">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Score label="Original" value={result.original_clarity} words={result.word_count.original} />
            <Score label="Retake" value={result.retake_clarity} words={result.word_count.retake} accent={delta != null ? (delta >= 0 ? "up" : "down") : null} />
          </div>
          <p className="text-sm text-slate-100 mb-3" data-testid="replay-coach-verdict">{result.verdict}</p>
          <blockquote className="text-xs text-slate-400 italic border-l-2 border-white/15 pl-2 mb-3 line-clamp-3">Your retake: “{result.retake}”</blockquote>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            {result.improved.length > 0 && <List icon={TrendingUp} color="text-emerald-400" title="Clearer" items={result.improved} />}
            {result.slipped.length > 0 && <List icon={TrendingDown} color="text-amber-400" title="Slipped" items={result.slipped} />}
          </div>
          {result.tighter_version && (
            <div className="rounded-md bg-white/5 border border-white/10 p-3 mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--aria-primary-glow)]">Say it like this next time</span>
                <button onClick={() => toggle(result.tighter_version, "tighter")} data-testid="replay-coach-listen-tighter" className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1">
                  {loadingKey === "tighter" ? <Loader2 size={10} className="animate-spin" /> : speaking && activeKey === "tighter" ? <Square size={10} /> : <Volume2 size={10} />} {speaking && activeKey === "tighter" ? "Stop" : "Hear it"}
                </button>
              </div>
              <p className="text-sm text-slate-100 leading-relaxed" data-testid="replay-coach-tighter">{result.tighter_version}</p>
            </div>
          )}
          {result.one_tip && <div className="text-xs text-slate-300 flex items-start gap-1.5"><Lightbulb size={12} className="text-amber-300 mt-0.5 shrink-0" /> {result.one_tip}</div>}
          <button onClick={() => { setResult(null); setRetake(""); }} data-testid="replay-coach-again" className="mt-3 text-xs text-[var(--aria-primary-glow)] hover:underline">Try another take</button>
        </div>
      )}
    </div>
  );
}

function Score({ label, value, words, accent }) {
  return (
    <div className="rounded-md bg-white/5 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`font-display text-2xl ${accent === "up" ? "text-emerald-400" : accent === "down" ? "text-amber-400" : ""}`}>{value ?? "—"}<span className="text-xs text-slate-500">/10</span></div>
      <div className="text-[10px] text-slate-500">{words} words</div>
    </div>
  );
}

function List({ icon: Icon, color, title, items }) {
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold mb-1 inline-flex items-center gap-1 ${color}`}><Icon size={11} /> {title}</div>
      <ul className="text-xs text-slate-300 space-y-0.5 list-disc pl-4">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  );
}
